import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, Tag, Button, Tooltip } from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  cursor: pointer;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .ant-card-cover {
    height: 200px;
    overflow: hidden;
    background-color: #f0f0f0;
  }

  .ant-card-cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EventImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  font-weight: bold;
`;

const EventTitle = styled.h3`
  font-weight: 600;
  color: #3c2f61;
  margin-bottom: 10px;
  font-size: 18px;
`;

const EventDescription = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: #666;
  font-size: 14px;

  .anticon {
    margin-right: 8px;
    color: #3c2f61;
  }
`;

const EventAmount = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #3c2f61;
  margin: 15px 0;
`;

const StatusTag = styled(Tag)`
  position: absolute;
  top: 10px;
  right: 10px;
`;

const EventCard = ({ event, onRefresh }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${month} ${day}${suffix}, ${year}`;
  };

  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const formatAmount = (amount, currencyCode) => {
    return `${currencyCode} ${amount.toLocaleString()}`;
  };

  const isUpcoming = new Date(event.event_date) > new Date();
  const isPast = new Date(event.event_date) < new Date();

  const renderEventImage = () => {
    if (event.image_url) {
      return (
        <img
          alt={event.name}
          src={event.image_url}
        />
      );
    }
    return (
      <EventImage>
        {event.name.charAt(0).toUpperCase()}
      </EventImage>
    );
  };

  const handleCardClick = () => {
    navigate(`/events/${event.id}`);
  };

  return (
    <StyledCard
      cover={renderEventImage()}
      onClick={handleCardClick}
      actions={[
        <Tooltip title="View Details" key="view">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            View Details
          </Button>
        </Tooltip>,
      ]}
    >
      <div style={{ position: "relative" }}>
        {isUpcoming && <StatusTag color="green">Upcoming</StatusTag>}
        {isPast && <StatusTag color="default">Past</StatusTag>}
        {!event.available && <StatusTag color="red">Cancelled</StatusTag>}
        
        <EventTitle>{event.name}</EventTitle>
        
        {event.description && (
          <EventDescription>{event.description}</EventDescription>
        )}

        <EventDetail>
          <CalendarOutlined />
          <span>{formatDate(event.event_date)}</span>
        </EventDetail>

        {event.physical_location && (
          <EventDetail>
            <EnvironmentOutlined />
            <span>{event.physical_location}</span>
          </EventDetail>
        )}

        <EventAmount>
        
          {formatAmount(event.amount, event.currency_code)}
          <span style={{ fontSize: "14px", fontWeight: "normal", color: "#666" }}>
            {" "}per person
          </span>
        </EventAmount>
      </div>
    </StyledCard>
  );
};

export default EventCard;